import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, BookOpen, ShieldCheck, Download } from "lucide-react";
import { Logo } from "@/components/store/Logo";
import { Starfield } from "@/components/store/Starfield";
import { track } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [failed, setFailed] = useState(false);
  const tracked = useRef(false);

  useEffect(() => {
    if (!sessionId) { setFailed(true); return; }
    let tries = 0;
    const poll = async () => {
      try {
        const s = await (await fetch(`${API}/payments/status/${sessionId}`)).json();
        if (s.payment_status === "paid") {
          const o = await (await fetch(`${API}/orders/by-session/${sessionId}`)).json();
          setOrder(o);
          if (!tracked.current) {
            tracked.current = true;
            track("Purchase", { value: o.total, currency: "USD", order_id: o.order_number });
            if (window.gtag) {
              window.gtag("event", "ads_conversion_Purchase_1", {
                value: o.total,
                currency: "USD",
                transaction_id: o.order_number,
              });
            }
          }
          return;
        }
      } catch {}
      tries += 1;
      if (tries > 15) setFailed(true);
      else setTimeout(poll, 2000);
    };
    poll();
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#07090e] px-4">
      <Starfield className="opacity-60" />
      <div className="absolute left-6 top-6"><Logo /></div>
      {!order && !failed && (
        <div className="relative text-center">
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-cyan-400" />
          <p className="mt-5 font-mono text-xs uppercase tracking-[0.25em] text-slate-400" data-testid="payment-confirming">Confirming your payment…</p>
        </div>
      )}
      {failed && (
        <div className="relative text-center">
          <p className="font-display text-2xl font-bold text-white">We couldn't confirm your payment yet.</p>
          <p className="mt-3 text-sm text-slate-400">If you completed payment, your confirmation email will arrive shortly.</p>
          <Link to="/" data-testid="success-home-link" className="mt-6 inline-block rounded-full border border-white/20 px-8 py-3 text-sm font-semibold text-white transition-colors hover:border-cyan-400/60 hover:text-cyan-300">Back to the store</Link>
        </div>
      )}
      {order && (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }} className="relative w-full max-w-md text-center">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.2 }} className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-emerald-400/15">
            <Check className="h-9 w-9 text-emerald-400" />
          </motion.div>
          <h1 className="font-display mt-6 text-3xl font-extrabold text-white sm:text-4xl">Your reset starts tonight.</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-400">
            Payment confirmed. The 7-Night Sleep Reset is ready — and a backup download link is on its way to{" "}
            <span className="text-slate-200">{order.email}</span>.
          </p>
          {order.download_token && (
            <a
              data-testid="download-guide-button"
              href={`${API}/download/${order.download_token}`}
              className="mt-7 flex items-center justify-center gap-2.5 rounded-full bg-cyan-400 px-8 py-4 text-base font-bold text-[#07090e] shadow-[0_0_40px_rgba(56,189,248,0.35)] transition-transform hover:scale-105 active:scale-95"
            >
              <Download className="h-5 w-5" /> Download Your Guide (PDF)
            </a>
          )}
          {order.bump_token && (
            <a
              data-testid="download-playbook-button"
              href={`${API}/download/${order.bump_token}`}
              className="mt-3 flex items-center justify-center gap-2.5 rounded-full border border-amber-400/50 bg-amber-400/10 px-8 py-3.5 text-sm font-bold text-amber-200 transition-colors hover:bg-amber-400/20"
            >
              <Download className="h-4 w-4" /> Download The 3AM Playbook (PDF)
            </a>
          )}
          <div className="mx-auto mt-6 rounded-2xl border border-white/10 bg-[#131a29] p-5 text-left text-sm" data-testid="success-order-card">
            <div className="flex justify-between"><span className="text-slate-400">Order</span><span className="font-mono font-bold text-cyan-300" data-testid="success-order-number">{order.order_number}</span></div>
            <div className="mt-2 flex justify-between"><span className="text-slate-400">Paid</span><span className="font-bold text-white">${order.total.toFixed(2)}</span></div>
            <div className="mt-2 flex justify-between"><span className="text-slate-400">Delivery</span><span className="text-emerald-400">Instant download</span></div>
          </div>
          <p className="mt-6 text-xs leading-relaxed text-slate-500">
            Tonight: read Night 1 (5 minutes), do the one exercise, and set your wind-down alarm. That's the whole job.
          </p>
          <div className="mt-6 flex items-center justify-center gap-5 text-xs text-slate-500">
            <span className="flex items-center gap-1"><BookOpen className="h-3.5 w-3.5" /> 16 pages, zero fluff</span>
            <span className="flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5" /> 30-night guarantee</span>
          </div>
          <Link to="/" data-testid="success-home-link-paid" className="mt-8 inline-block rounded-full border border-white/20 px-8 py-3 text-sm font-semibold text-white transition-colors hover:border-cyan-400/60 hover:text-cyan-300">
            Back to the Cosmos
          </Link>
        </motion.div>
      )}
    </div>
  );
}
